import {
  isValidTemplateSlotParamName,
  type TemplateSlotDeclaredParam,
} from "./playbook-workflow-extensions/editor-extension-template-slot";

export const TEMPLATE_PARAM_TYPES = ["string", "text", "boolean", "enum"] as const;

export type TemplateParamType = (typeof TEMPLATE_PARAM_TYPES)[number];

export interface TemplateParamDefinition extends TemplateSlotDeclaredParam {
  type: TemplateParamType;
  required: boolean;
  enumValues?: string[];
}

export type TemplateAuthoringValidationWarning =
  | { code: "UNDECLARED_SLOT"; param: string }
  | { code: "UNDECLARED_CONDITIONAL"; param: string }
  | { code: "NON_BOOLEAN_CONDITIONAL"; param: string; type: TemplateParamType }
  | { code: "SUBJECT_NOT_STRING"; param: string; type: TemplateParamType }
  | { code: "WORKFLOW_NODE_IN_TEMPLATE"; param: string }
  | { code: "EMPTY_ENUM_PARAM"; param: string }
  | { code: "INVALID_PARAM_NAME"; param: string }
  | { code: "DUPLICATE_PARAM_NAME"; param: string }
  | { code: "UNPLACED_REQUIRED_PARAM"; param: string };

// A param named exactly "subject" sets the outbound email subject and the backend
// schema validation rejects it unless it is type "string" (a 400 on save), so treat
// it as an author-time, save-blocking error rather than a late server rejection.
export const TEMPLATE_SUBJECT_PARAM_NAME = "subject";

const TEMPLATE_PARAM_TYPE_SET = new Set<string>(TEMPLATE_PARAM_TYPES);

const normalizeTemplateParamName = (value: unknown): string => String(value ?? "").trim();

const normalizeTemplateParamLabel = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed || undefined;
};

const normalizeEnumValues = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  const seen = new Set<string>();
  const values: string[] = [];

  for (const rawValue of value) {
    const enumValue = String(rawValue ?? "").trim();
    if (!enumValue || seen.has(enumValue)) {
      continue;
    }

    seen.add(enumValue);
    values.push(enumValue);
  }

  return values;
};

export const isTemplateParamType = (value: unknown): value is TemplateParamType =>
  typeof value === "string" && TEMPLATE_PARAM_TYPE_SET.has(value);

export const createTemplateParamDefinition = (
  existingParams: TemplateParamDefinition[] = []
): TemplateParamDefinition => {
  const existingNames = new Set(existingParams.map((param) => param.name));
  let index = existingParams.length + 1;
  let name = `param${index}`;

  while (existingNames.has(name)) {
    index += 1;
    name = `param${index}`;
  }

  return { name, type: "string", required: false };
};

export const parseTemplateParamSchema = (schema: unknown): TemplateParamDefinition[] => {
  if (!Array.isArray(schema)) {
    return [];
  }

  const seen = new Set<string>();
  const params: TemplateParamDefinition[] = [];

  for (const rawDefinition of schema) {
    if (!rawDefinition || typeof rawDefinition !== "object") {
      continue;
    }

    const definition = rawDefinition as Record<string, unknown>;
    const name = normalizeTemplateParamName(definition.name);

    if (!isValidTemplateSlotParamName(name) || seen.has(name)) {
      continue;
    }

    const type = isTemplateParamType(definition.type) ? definition.type : "string";
    const label = normalizeTemplateParamLabel(definition.label);
    const enumValues = type === "enum" ? normalizeEnumValues(definition.enumValues) : undefined;

    seen.add(name);
    params.push({
      name,
      type,
      required: definition.required === true,
      ...(enumValues ? { enumValues } : {}),
      ...(label ? { label } : {}),
    });
  }

  return params;
};

export const getDeclaredTemplateParams = (params: TemplateParamDefinition[]): TemplateParamDefinition[] => {
  const seen = new Set<string>();
  const declaredParams: TemplateParamDefinition[] = [];

  for (const param of params) {
    const name = normalizeTemplateParamName(param.name);

    if (!isValidTemplateSlotParamName(name) || seen.has(name)) {
      continue;
    }

    seen.add(name);
    declaredParams.push({
      name,
      type: param.type,
      required: param.required === true,
      ...(param.label ? { label: param.label } : {}),
      ...(param.type === "enum" ? { enumValues: normalizeEnumValues(param.enumValues) } : {}),
    });
  }

  return declaredParams;
};

export const getTemplateConditionalParams = (params: TemplateParamDefinition[]): TemplateParamDefinition[] =>
  getDeclaredTemplateParams(params).filter((param) => param.type === "boolean");

/**
 * Nodes that only make sense inside an Alby workflow. They serialize to internal
 * markup the deterministic template renderer does not strip, so a template body
 * carrying them would emit raw tool names / file IDs into an outbound email.
 */
const WORKFLOW_ONLY_NODE_SELECTORS: ReadonlyArray<readonly [string, string]> = [
  ['[data-type="chat-tool-name"]', "tool name"],
  ['[data-type="playbook-file"]', "playbook file"],
];

const hasMatchingElement = (bodyHtml: string, selector: string): boolean => {
  if (!bodyHtml) {
    return false;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(bodyHtml, "text/html");
  return doc.querySelector(selector) !== null;
};

/**
 * Whether a body still carries template markup. Used to guard the reverse
 * conversion: outside a Template these nodes are inert but not harmless —
 * workflow consumers flatten the HTML with `workflowHtmlToPlainText()`, so slots
 * would read as literal param names and a conditional's contents would always be
 * included rather than depending on its flag.
 */
export const hasTemplateMarkup = (bodyHtml: string): boolean =>
  hasMatchingElement(bodyHtml, "[data-tpl-slot]") || hasMatchingElement(bodyHtml, "[data-tpl-if]");

const getTemplateAttributeRefs = (bodyHtml: string, attributeName: "data-tpl-slot" | "data-tpl-if"): string[] => {
  if (!bodyHtml) {
    return [];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(bodyHtml, "text/html");
  const refs: string[] = [];

  doc.querySelectorAll(`[${attributeName}]`).forEach((node) => {
    const param = normalizeTemplateParamName(node.getAttribute(attributeName));
    if (param) {
      refs.push(param);
    }
  });

  return refs;
};

const uniqueInOrder = (values: string[]): string[] => {
  const seen = new Set<string>();
  const uniqueValues: string[] = [];

  for (const value of values) {
    if (seen.has(value)) {
      continue;
    }

    seen.add(value);
    uniqueValues.push(value);
  }

  return uniqueValues;
};

export const isTemplateAuthoringValidationSaveBlocking = (warning: TemplateAuthoringValidationWarning): boolean =>
  // Block save on anything the backend hard-rejects, so the author fixes it here
  // instead of hitting a 400 on save (schema: subject type, enum, param names) or a
  // TEMPLATE_SCHEMA_INVALID at fill/send (body references an undeclared param or a
  // non-boolean conditional). UNPLACED_REQUIRED_PARAM stays a soft warning — such a
  // template still saves and renders.
  warning.code === "EMPTY_ENUM_PARAM" ||
  warning.code === "INVALID_PARAM_NAME" ||
  warning.code === "DUPLICATE_PARAM_NAME" ||
  warning.code === "SUBJECT_NOT_STRING" ||
  warning.code === "WORKFLOW_NODE_IN_TEMPLATE" ||
  warning.code === "UNDECLARED_SLOT" ||
  warning.code === "UNDECLARED_CONDITIONAL" ||
  warning.code === "NON_BOOLEAN_CONDITIONAL";

export const getSaveBlockingTemplateAuthoringWarnings = (
  warnings: TemplateAuthoringValidationWarning[]
): TemplateAuthoringValidationWarning[] => warnings.filter(isTemplateAuthoringValidationSaveBlocking);

export const validateTemplateAuthoring = ({
  bodyHtml,
  declaredParams,
}: {
  bodyHtml: string;
  declaredParams: TemplateParamDefinition[];
}): TemplateAuthoringValidationWarning[] => {
  const params = getDeclaredTemplateParams(declaredParams);
  const declaredByName = new Map(params.map((param) => [param.name, param]));
  const slotRefs = new Set(uniqueInOrder(getTemplateAttributeRefs(bodyHtml, "data-tpl-slot")));
  const conditionalRefs = uniqueInOrder(getTemplateAttributeRefs(bodyHtml, "data-tpl-if"));
  const placedRefs = new Set([...slotRefs, ...conditionalRefs]);
  const warnings: TemplateAuthoringValidationWarning[] = [];
  const seenParamNames = new Set<string>();

  // Workflow-only nodes (chat tool names, playbook file chips) are meaningless in a
  // deterministic email body and the backend renderer doesn't strip them, so
  // fillTemplate would emit their internal markup verbatim into the outbound email.
  // Converting a Workflow to a Template can carry them in, so block the save and let
  // the author remove them rather than silently rewriting their body.
  for (const [selector, label] of WORKFLOW_ONLY_NODE_SELECTORS) {
    if (hasMatchingElement(bodyHtml, selector)) {
      warnings.push({ code: "WORKFLOW_NODE_IN_TEMPLATE", param: label });
    }
  }

  for (const param of declaredParams) {
    const name = normalizeTemplateParamName(param.name);

    if (!isValidTemplateSlotParamName(name)) {
      warnings.push({ code: "INVALID_PARAM_NAME", param: name });
      continue;
    }

    if (seenParamNames.has(name)) {
      warnings.push({ code: "DUPLICATE_PARAM_NAME", param: name });
      continue;
    }

    seenParamNames.add(name);
  }

  for (const param of slotRefs) {
    if (!declaredByName.has(param)) {
      warnings.push({ code: "UNDECLARED_SLOT", param });
    }
  }

  for (const param of conditionalRefs) {
    const definition = declaredByName.get(param);

    if (!definition) {
      warnings.push({ code: "UNDECLARED_CONDITIONAL", param });
      continue;
    }

    if (definition.type !== "boolean") {
      warnings.push({ code: "NON_BOOLEAN_CONDITIONAL", param, type: definition.type });
    }
  }

  for (const param of params) {
    if (normalizeTemplateParamName(param.name) === TEMPLATE_SUBJECT_PARAM_NAME && param.type !== "string") {
      warnings.push({ code: "SUBJECT_NOT_STRING", param: param.name, type: param.type });
    }

    if (param.type === "enum" && normalizeEnumValues(param.enumValues).length === 0) {
      warnings.push({ code: "EMPTY_ENUM_PARAM", param: param.name });
    }

    // `subject` is consumed by fillTemplate to set the outbound email subject, so a
    // required `subject` with no data-tpl-slot in the body is correct, not an
    // oversight — don't flag it as unplaced.
    if (
      param.required &&
      !placedRefs.has(param.name) &&
      normalizeTemplateParamName(param.name) !== TEMPLATE_SUBJECT_PARAM_NAME
    ) {
      warnings.push({ code: "UNPLACED_REQUIRED_PARAM", param: param.name });
    }
  }

  return warnings;
};

export const getTemplateAuthoringValidationMessage = (warning: TemplateAuthoringValidationWarning): string => {
  switch (warning.code) {
    case "UNDECLARED_SLOT":
      return `Slot "${warning.param}" is not declared.`;
    case "UNDECLARED_CONDITIONAL":
      return `Conditional "${warning.param}" is not declared.`;
    case "NON_BOOLEAN_CONDITIONAL":
      return `Conditional "${warning.param}" uses a ${warning.type} param, but conditionals require boolean params.`;
    case "WORKFLOW_NODE_IN_TEMPLATE":
      return `Remove the ${warning.param} from the body — it belongs to workflows and would be sent as raw markup in the email.`;
    case "SUBJECT_NOT_STRING":
      return `The "subject" param sets the email subject and must be type "string", not ${warning.type}.`;
    case "EMPTY_ENUM_PARAM":
      return `Enum param "${warning.param}" needs at least one value.`;
    case "INVALID_PARAM_NAME":
      return warning.param ? `Param name "${warning.param}" is invalid.` : "Param name is required.";
    case "DUPLICATE_PARAM_NAME":
      return `Param name "${warning.param}" is declared more than once.`;
    case "UNPLACED_REQUIRED_PARAM":
      return `Required param "${warning.param}" is not placed in the template body.`;
  }
};
